import * as Sentry from '@sentry/react';
import i18next, { TFunction } from 'i18next';
import { match } from 'ts-pattern';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useEffect } from 'react';

export default function setupSentry(t: TFunction) {
  Sentry.init({
    dsn: import.meta.env.REACT_APP_SENTRY_DSN,
    environment: match(import.meta.env.REACT_APP_BRANCH)
      .with('main', 'master', 'live', (branch) =>
        import.meta.env.MODE === 'production' ? 'production' : `${branch} (${import.meta.env.MODE})`
      )
      .otherwise((branch) =>
        import.meta.env.MODE === 'production' ? branch : `${branch} (${import.meta.env.MODE})`
      ),
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.extraErrorDataIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'light',
        themeLight: {
          accentForeground: 'var(--dark-color)',
          accentBackground: 'var(--primary-color)',
        },
        triggerLabel: t('component.sentryFeedback.triggerLabel'),
        triggerAriaLabel: t('component.sentryFeedback.triggerAriaLabel'),
        formTitle: t('component.sentryFeedback.formTitle'),
        submitButtonLabel: t('component.sentryFeedback.submitButtonLabel'),
        cancelButtonLabel: t('component.sentryFeedback.cancelButtonLabel'),
        confirmButtonLabel: t('component.sentryFeedback.confirmButtonLabel'),
        addScreenshotButtonLabel: t('component.sentryFeedback.addScreenshotButtonLabel'),
        removeScreenshotButtonLabel: t('component.sentryFeedback.removeScreenshotButtonLabel'),
        nameLabel: t('component.sentryFeedback.nameLabel'),
        namePlaceholder: t('component.sentryFeedback.namePlaceholder'),
        emailLabel: t('component.sentryFeedback.emailLabel'),
        emailPlaceholder: t('component.sentryFeedback.emailPlaceholder'),
        isRequiredLabel: t('component.sentryFeedback.isRequiredLabel'),
        messageLabel: t('component.sentryFeedback.messageLabel'),
        messagePlaceholder: t('component.sentryFeedback.messagePlaceholder'),
        successMessageText: t('component.sentryFeedback.successMessageText'),
        highlightToolText: t('component.sentryFeedback.highlightToolText'),
        hideToolText: t('component.sentryFeedback.hideToolText'),
        removeHighlightText: t('component.sentryFeedback.removeHighlightText'),
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      if (!event.tags) event.tags = {};
      // track user language
      if (i18next.isInitialized) event.tags.locale = i18next.language;
      // track platform
      event.tags.platform =
        window.ReactNativeWebViewConstants && window.ReactNativeWebViewConstants.PLATFORM_OS
          ? 'webview'
          : 'browser';
      event.tags.version = import.meta.env.REACT_APP_VERSION_STRING;
      return event;
    },
  });
}
